import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';

import { IErrorResponse } from 'common/models/api-service/error-response.model';
import { ApiStatus, Action, EntityType } from 'common/enums';
import { PagedRequest } from 'common/models/data/pagedRequest.model';

import initialPreviewDataState from './previewData.state';
import { FieldError } from 'common/models';
import { CategoryDTO, PreviewEntityDTO } from 'common/models/data/entity.model';
import {
    addCategories,
    findCategories,
    removeCategories,
    toggleExpansion,
    updateCategory,
    updateErrorFixedStatus,
    updateParentErrorFixedStatus,
    updateParentErrorFixedStatusOnDelete
} from 'common/utils/table';
import { SearchDTO, SearchVM } from 'common/models/data/search.model';
import { PaginatedApiResponse } from 'common/models/api-service/api-response.model';
import { IndicatorUpdateDTO } from 'common/models/data/indicator.model';
import { EntityDetailsPayload } from 'common/models/data/entityStore.model';
import { ApiInitiator } from 'common/enums/data.enum';

const previewDataSlice = createSlice({
    name: 'previewData',
    initialState: initialPreviewDataState,
    reducers: {
        loadPreviewData: (state, { payload }: PayloadAction<PagedRequest>) => {
            state.previewData.status = ApiStatus.LOADING;
        },
        loadPreviewDataSuccess: (state, { payload }: PayloadAction<any>) => {
            state.previewData.status = ApiStatus.SUCCESS;
            if (payload.pagination.currentPage === 1) {
                state.previewData.data = payload;
            } else {
                if (
                    state.previewData.data &&
                    state.previewData.data.data &&
                    state.previewData.data.data.length > 0 &&
                    payload.data &&
                    payload.data.length > 0
                ) {
                    let updatedData = [...current(state.previewData.data.data)];
                    updatedData = updatedData.concat([...payload.data]);
                    state.previewData.data.data = updatedData;
                    state.previewData.data.pagination = payload.pagination;
                }
            }
        },
        loadPreviewDataError: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.previewData.status = ApiStatus.ERROR;
        },
        setFieldErrors: (state, { payload }: PayloadAction<Array<FieldError>>) => {
            state.fieldErrors = payload;
        },
        deleteData: (state, { payload }: PayloadAction<EntityDetailsPayload>) => {
            state.deleteApiResponse.status = ApiStatus.LOADING;
        },
        deleteDataSuccess: (state, { payload }: PayloadAction<PreviewEntityDTO>) => {
            state.deleteApiResponse.status = ApiStatus.SUCCESS;
            const deletedEntity = findCategories(state.previewData.data?.data, payload.id);
            if (state.previewData.data?.data)
                state.previewData.data.data = removeCategories(
                    state.previewData.data?.data,
                    payload.id
                );
            if (deletedEntity.hasError) {
                if (state.previewData.data?.data && deletedEntity.parentId) {
                    state.previewData.data.data = updateParentErrorFixedStatusOnDelete(
                        state.previewData.data.data,
                        deletedEntity.parentId
                    );
                }
            }
            if (state.searchList.data?.data && state.searchList.data.data.length > 0) {
                const searchListIndex = state.searchList.data.data.findIndex(
                    (sl) => sl.id === payload.id
                );
                state.searchList.data.data.splice(searchListIndex, 1);
            }
        },
        deleteDataError: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.deleteApiResponse.status = ApiStatus.ERROR;
        },
        toggleExpansion: (state, { payload }: PayloadAction<PreviewEntityDTO>) => {
            if (state.previewData.data?.data)
                state.previewData.data.data = toggleExpansion(
                    payload,
                    state.previewData.data?.data,
                    ApiInitiator.PreviewData
                );
        },
        toggleExpansionSuccess: (state, { payload }: PayloadAction<any>) => {
            const entity = findCategories(state.previewData.data?.data, payload.parent.id);
            if (
                entity.subCategories === undefined ||
                (entity.subCategories && entity.subCategories.length === 0)
            ) {
                if (state.previewData.data?.data)
                    state.previewData.data.data = addCategories(
                        state.previewData.data.data,
                        payload.parent.id,
                        payload.subCategories
                    );
            }
        },
        toggleExpansionFailed: (state) => {
            return state;
        },
        updateCategory: (state, { payload }: PayloadAction<CategoryDTO>) => {
            state.editStatus.status = ApiStatus.LOADING;
        },
        updateCategorySuccess: (state, { payload }: PayloadAction<CategoryDTO>) => {
            state.editStatus.status = ApiStatus.SUCCESS;

            const updatedEntity: PreviewEntityDTO = findCategories(
                state.previewData.data?.data,
                payload.categoryId
            );

            updatedEntity.name = payload.categoryName;
            updatedEntity.order = payload.categoryOrder.toString();
            updatedEntity.description = payload.description;

            if (updatedEntity && updatedEntity?.errors?.length > 0 && updatedEntity.hasError) {
                updatedEntity.isErrorFixed = true;
                updatedEntity.errors = null;
                state.isAtleastOneErrorFixed = true;
            }
            if (state.previewData.data?.data)
                state.previewData.data.data = updateCategory(
                    state.previewData.data?.data,
                    updatedEntity.id,
                    updatedEntity
                );
            if (updatedEntity.hasError) {
                if (state.previewData.data?.data)
                    state.previewData.data.data = updateErrorFixedStatus(
                        state.previewData.data.data,
                        updatedEntity.id
                    );
            }
            if (state.searchList.data?.data && state.searchList.data.data.length > 0) {
                const searchListIndex = state.searchList.data.data.findIndex(
                    (sl) => sl.id === updatedEntity.id
                );
                state.searchList.data.data[searchListIndex].name = updatedEntity.name;
                state.searchList.data.data[searchListIndex].description = updatedEntity.description;
            }

            state.editData = undefined;
            state.currentAction = Action.Default;
        },
        updateCategoryFailed: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.editStatus.status = ApiStatus.ERROR;
            state.editStatus.error = payload;
            state.editData = undefined;
            state.currentAction = Action.Default;
        },
        updateSubCategory: (state, { payload }: PayloadAction<CategoryDTO>) => {
            state.editStatus.status = ApiStatus.LOADING;
        },
        updateSubCategorySuccess: (state, { payload }: PayloadAction<CategoryDTO>) => {
            state.editStatus.status = ApiStatus.SUCCESS;
            const updatedEntity: PreviewEntityDTO = findCategories(
                state.previewData.data?.data,
                payload.categoryId
            );
            if (updatedEntity.errors?.length > 0 && updatedEntity.hasError) {
                updatedEntity.isErrorFixed = true;
                updatedEntity.errors = null;
                state.isAtleastOneErrorFixed = true;
            }
            updatedEntity.id = payload.categoryId;
            updatedEntity.name = payload.categoryName;
            updatedEntity.order = payload.categoryOrder.toString();
            updatedEntity.description = payload.description;

            if (state.previewData.data?.data)
                state.previewData.data.data = updateCategory(
                    state.previewData.data.data,
                    updatedEntity.id,
                    updatedEntity
                );
            if (updatedEntity.hasError) {
                if (state.previewData.data?.data) {
                    state.previewData.data.data = updateErrorFixedStatus(
                        state.previewData.data.data,
                        updatedEntity.id
                    );
                    state.previewData.data.data = updateParentErrorFixedStatus(
                        state.previewData.data.data,
                        updatedEntity.id
                    );
                }
            }
            if (state.searchList.data?.data && state.searchList.data.data.length > 0) {
                const searchListIndex = state.searchList.data.data.findIndex(
                    (sl) => sl.id === updatedEntity.id
                );
                state.searchList.data.data[searchListIndex].name = updatedEntity.name;
                state.searchList.data.data[searchListIndex].description = updatedEntity.description;
            }
            state.editData = undefined;
            state.currentAction = Action.Default;
        },
        updateSubCategoryFailed: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.editStatus.status = ApiStatus.ERROR;
            state.editStatus.error = payload;
            state.editData = undefined;
        },
        updateIndicator: (state, { payload }: PayloadAction<IndicatorUpdateDTO>) => {
            state.editStatus.status = ApiStatus.LOADING;
        },
        updateIndicatorSuccess: (state, { payload }: PayloadAction<any>) => {
            state.editStatus.status = ApiStatus.SUCCESS;
            const updatedEntity = findCategories(state.previewData.data?.data, payload.id);
            if (updatedEntity && updatedEntity.id) {
                updatedEntity.name = payload.name;
                updatedEntity.order = payload.order;
                if (updatedEntity.hasError) {
                    updatedEntity.isErrorFixed = true;
                    updatedEntity.hasError = false;
                    updatedEntity.errors = null;
                    state.isAtleastOneErrorFixed = true;
                }
                if (state.previewData.data?.data) {
                    state.previewData.data.data = updateCategory(
                        state.previewData.data.data,
                        updatedEntity.id ?? '',
                        updatedEntity
                    );

                    state.previewData.data.data = updateParentErrorFixedStatus(
                        state.previewData.data.data,
                        updatedEntity.parentId
                    );
                }
                if (state.searchList.data?.data && state.searchList.data.data.length > 0) {
                    const searchListIndex = state.searchList.data.data.findIndex(
                        (sl) => sl.id === updatedEntity.id
                    );
                    if (updatedEntity && updatedEntity.name)
                        state.searchList.data.data[searchListIndex].name = updatedEntity.name;
                }
            }
            state.editData = undefined;
            state.currentAction = Action.Default;
        },
        updateIndicatorFailed: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.editStatus.status = ApiStatus.ERROR;
            state.editStatus.error = payload;
            state.editData = undefined;
        },
        searchList: (state, { payload }: PayloadAction<SearchDTO>) => {
            state.searchList.status = ApiStatus.LOADING;
        },
        searchListSuccess: (
            state,
            { payload }: PayloadAction<PaginatedApiResponse<SearchVM[]>>
        ) => {
            state.searchList.status = ApiStatus.SUCCESS;
            if (
                state.searchList.data &&
                state.searchList.data?.data &&
                state.searchList.data?.data.length > 0
            ) {
                if (payload.pagination.currentPage === 1) {
                    state.searchList.data.data = payload.data;
                } else {
                    if (payload.data) state.searchList.data?.data.push(...payload.data);
                }
                state.searchList.data.pagination = payload.pagination;
            } else {
                state.searchList.data = payload;
            }
        },
        searchListError: (state) => {
            state.searchList.status = ApiStatus.ERROR;
        },
        resetToInitial: (state) => {
            state.previewData = {};
        },
        setCurrentAction: (state, { payload }: PayloadAction<number>) => {
            state.currentAction = payload;
        },
        fetchErrorCount: (state) => {
            state.errorCountFetchStatus.status = ApiStatus.LOADING;
        },
        fetchErrorCountSuccess: (state, { payload }: PayloadAction<any>) => {
            state.errorCountFetchStatus.status = ApiStatus.SUCCESS;
            state.errorCount = payload;
        },
        fetchErrorCountFailed: (state) => {
            state.errorCountFetchStatus.status = ApiStatus.ERROR;
        }
    }
});

export default previewDataSlice.reducer;
export const previewDataActions = previewDataSlice.actions;
